import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { 
  NotificationPreference, 
  UpdateNotificationPreferenceRequest, 
  NotificationHistoryItem,
  NotificationType
} from '../types';
import { notificationApi } from '../services/notificationApi';
import toast from 'react-hot-toast';

interface NotificationState {
  // State
  preferences: NotificationPreference[];
  history: NotificationHistoryItem[];
  isLoading: boolean;
  error: string | null;
  browserPushSupported: boolean;
  browserPushPermission: NotificationPermission;

  // Actions
  loadPreferences: () => Promise<void>;
  updatePreference: (type: NotificationType, request: UpdateNotificationPreferenceRequest) => Promise<boolean>;
  loadHistory: (limit?: number) => Promise<void>;
  sendTestNotification: (type: NotificationType) => Promise<void>;
  requestBrowserPushPermission: () => Promise<boolean>;
  checkBrowserPushSupport: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      // Initial state
      preferences: [],
      history: [],
      isLoading: false,
      error: null,
      browserPushSupported: notificationApi.isBrowserPushSupported(),
      browserPushPermission: notificationApi.getBrowserPushPermission(),

      // Actions
      loadPreferences: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const preferences = await notificationApi.getPreferences();
          set({
            preferences,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load preferences';
          set({
            error: errorMessage,
            isLoading: false,
          });
          toast.error(errorMessage);
        }
      },

      updatePreference: async (type: NotificationType, request: UpdateNotificationPreferenceRequest) => {
        set({ isLoading: true, error: null });
        
        try {
          const updatedPreference = await notificationApi.updatePreference(type, request);
          
          set((state) => ({
            preferences: state.preferences.map(pref => 
              pref.type === type ? updatedPreference : pref
            ),
            isLoading: false,
            error: null,
          }));
          
          toast.success('Notification preference updated successfully!');
          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update preference';
          set({
            error: errorMessage,
            isLoading: false,
          });
          toast.error(errorMessage);
          return false;
        }
      },

      loadHistory: async (limit = 50) => {
        set({ isLoading: true, error: null });
        
        try {
          const history = await notificationApi.getHistory(limit);
          set({
            history,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load notification history';
          set({
            error: errorMessage,
            isLoading: false,
          });
          toast.error(errorMessage);
        }
      },

      sendTestNotification: async (type: NotificationType) => {
        set({ isLoading: true, error: null });
        
        try {
          await notificationApi.sendTestNotification(type);
          set({
            isLoading: false,
            error: null,
          });
          toast.success('Test notification sent!');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to send test notification';
          set({
            error: errorMessage,
            isLoading: false,
          });
          toast.error(errorMessage);
        }
      },

      requestBrowserPushPermission: async () => {
        try {
          const granted = await notificationApi.requestBrowserPushPermission();
          set({
            browserPushPermission: notificationApi.getBrowserPushPermission(),
          });
          
          if (granted) {
            toast.success('Browser notifications enabled!');
          } else {
            toast.error('Browser notifications permission denied');
          }
          
          return granted;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to request permission';
          toast.error(errorMessage);
          return false;
        }
      },

      checkBrowserPushSupport: () => {
        const supported = notificationApi.isBrowserPushSupported();
        const permission = notificationApi.getBrowserPushPermission();
        
        set({
          browserPushSupported: supported,
          browserPushPermission: permission,
        });
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'notification-store',
    }
  )
); 